import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, inrShort, pct, fmtDate, fmtDateLong } from "@/lib/format";
import { Badge, Bar } from "@/components/Badge";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Camera, IndianRupee, Loader2, ChevronRight, ChevronDown,
  Megaphone, Image, ListChecks, CalendarRange,
} from "lucide-react";
import { PendingWorkTable } from "@/components/PendingWorkTable";
import { InProgressNow } from "@/components/InProgressNow";
import { UpcomingFestivals } from "@/components/UpcomingFestivals";

function Section({ title, subtitle, children, action, danger, warn }) {
  const border = danger ? "border-red-200" : warn ? "border-amber-200" : "border-zinc-200";
  return (
    <Card className={`rounded-lg ${border} shadow-sm`}>
      <div className="flex items-center justify-between border-b border-zinc-100 px-5 py-4">
        <div>
          <h3 className="font-heading text-base font-semibold tracking-tight">{title}</h3>
          {subtitle && <p className="mt-0.5 text-xs text-zinc-500">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </Card>
  );
}

function ClientHealthCard({ h, onClick }) {
  const c = h.health;
  const ring = c === "red" ? "border-red-200 bg-red-50/40" : c === "orange" ? "border-amber-200 bg-amber-50/40" : "border-emerald-200 bg-emerald-50/30";
  return (
    <button onClick={onClick} data-testid={`health-card-${h.client_id}`} className={`flex w-full items-center gap-4 rounded-lg border ${ring} p-3 text-left transition-transform hover:scale-[1.01]`}>
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-zinc-900">{h.client_name}</div>
        <div className="text-xs text-zinc-500">{h.posted}/{h.planned} posted · {h.reason || "On track"}</div>
      </div>
      <div className="w-24 shrink-0">
        <Bar value={h.actual_progress} color={c} />
        <div className="mt-1 text-right text-xs font-medium text-zinc-600">{pct(h.actual_progress)}</div>
      </div>
      <ChevronRight className="h-4 w-4 shrink-0 text-zinc-300" />
    </button>
  );
}

function MiniStat({ label, value, tone = "zinc" }) {
  const map = { zinc: "text-zinc-900", green: "text-emerald-600", orange: "text-amber-600", red: "text-red-600", blue: "text-blue-600" };
  return (
    <div className="rounded-md border border-zinc-100 p-3">
      <div className={`metric-value text-2xl font-semibold ${map[tone]}`}>{value}</div>
      <div className="mt-0.5 text-xs text-zinc-500">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showGreen, setShowGreen] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });

  if (isLoading || !data)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;

  const s = data.summary;
  const w = data.week;
  const c = s.content;
  const t = s.tasks;
  const money = data.money;
  const isManager = data.is_manager;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Good morning, {user?.name?.split(" ")[0]}</h1>
          <p className="mt-1 flex items-center gap-2 text-sm text-zinc-500">
            <CalendarRange className="h-4 w-4" /> Current week · {fmtDate(w.monday)} → {fmtDateLong(w.saturday)}
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-4 py-2">
          <span className="text-xs text-zinc-500">Expected progress</span>
          <span className="font-heading text-sm font-semibold">{pct(w.expected_progress)}</span>
        </div>
      </div>

      <InProgressNow />

      <UpcomingFestivals days={45} showCoverage />

      <PendingWorkTable />

      <Section title="🔴 Critical Clients" subtitle="Seriously behind this week — act now" danger>
        {data.health.critical.length === 0 ? (
          <div className="py-4 text-center text-sm text-zinc-400">No critical clients right now 🎉</div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {data.health.critical.map((h) => <ClientHealthCard key={h.client_id} h={h} onClick={() => navigate(`/clients/${h.client_id}`)} />)}
          </div>
        )}
      </Section>

      <Section title="🟠 Needs Attention" subtitle="Partially complete or behind expected progress" warn>
        {data.health.attention.length === 0 ? (
          <div className="py-4 text-center text-sm text-zinc-400">Nothing needs attention</div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {data.health.attention.map((h) => <ClientHealthCard key={h.client_id} h={h} onClick={() => navigate(`/clients/${h.client_id}`)} />)}
          </div>
        )}
      </Section>

      <Collapsible open={showGreen} onOpenChange={setShowGreen}>
        <Card className="rounded-lg border-zinc-200 shadow-sm">
          <CollapsibleTrigger asChild>
            <button className="flex w-full items-center justify-between px-5 py-4" data-testid="ontrack-toggle">
              <div className="flex items-center gap-2">
                <h3 className="font-heading text-base font-semibold">🟢 On Track</h3>
                <Badge color="green">{data.health.on_track.length}</Badge>
              </div>
              <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${showGreen ? "rotate-180" : ""}`} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="grid grid-cols-1 gap-3 border-t border-zinc-100 p-5 md:grid-cols-2 lg:grid-cols-3">
              {data.health.on_track.map((h) => <ClientHealthCard key={h.client_id} h={h} onClick={() => navigate(`/clients/${h.client_id}`)} />)}
            </div>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="Content Work — This Week" subtitle="From the content calendar" action={<Megaphone className="h-4 w-4 text-zinc-400" />}>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-7">
            <MiniStat label="Planned" value={c.planned} tone="blue" />
            <MiniStat label="In Progress" value={c.in_progress} tone="orange" />
            <MiniStat label="Ready" value={c.ready_for_approval} tone="blue" />
            <MiniStat label="Approved" value={c.approved} tone="green" />
            <MiniStat label="Posted" value={c.posted} tone="green" />
            <MiniStat label="Pending" value={c.pending} tone="orange" />
            <MiniStat label="Delayed" value={c.delayed} tone="red" />
          </div>
        </Section>
        <Section title="General Tasks" subtitle="Assigned directly by management" action={<ListChecks className="h-4 w-4 text-zinc-400" />}>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
            <MiniStat label="Assigned" value={t.assigned} tone="blue" />
            <MiniStat label="In Progress" value={t.in_progress} tone="orange" />
            <MiniStat label="Completed" value={t.completed} tone="green" />
            <MiniStat label="Pending" value={t.pending} tone="orange" />
            <MiniStat label="Overdue" value={t.overdue} tone="red" />
          </div>
        </Section>
      </div>

      {isManager && data.team_progress && (
        <Section title="🧑‍💼 Team Work Progress" subtitle="Where work is happening this week — kept separate by work type" action={<button onClick={() => navigate("/team-progress")} className="text-xs font-medium text-zinc-500 hover:text-zinc-900" data-testid="view-team-progress">View Team Progress →</button>}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
            <div className="rounded-md border border-zinc-100 p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-blue-600">Social Media</div>
              <div className="space-y-1 text-sm text-zinc-600"><div className="flex justify-between"><span>Posted today</span><b>{data.team_progress.social_media.posted_today}</b></div><div className="flex justify-between"><span>To publish</span><b>{data.team_progress.social_media.to_publish}</b></div><div className="flex justify-between"><span>Stories</span><b>{data.team_progress.social_media.stories_posted}</b></div></div>
            </div>
            <div className="rounded-md border border-zinc-100 p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-purple-600">Graphic Design</div>
              <div className="space-y-1 text-sm text-zinc-600"><div className="flex justify-between"><span>Pending</span><b>{data.team_progress.design.pending}</b></div><div className="flex justify-between"><span>In progress</span><b>{data.team_progress.design.in_progress}</b></div><div className="flex justify-between"><span>Ready</span><b>{data.team_progress.design.ready_for_approval}</b></div></div>
            </div>
            <div className="rounded-md border border-zinc-100 p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-rose-600">Video Editing</div>
              <div className="space-y-1 text-sm text-zinc-600"><div className="flex justify-between"><span>Pending</span><b>{data.team_progress.video.pending}</b></div><div className="flex justify-between"><span>In progress</span><b>{data.team_progress.video.in_progress}</b></div><div className="flex justify-between"><span>Ready</span><b>{data.team_progress.video.ready_for_approval}</b></div></div>
            </div>
            <div className="rounded-md border border-zinc-100 p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-600">General Tasks</div>
              <div className="space-y-1 text-sm text-zinc-600"><div className="flex justify-between"><span>In progress</span><b>{data.team_progress.tasks.in_progress}</b></div><div className="flex justify-between"><span>Completed</span><b>{data.team_progress.tasks.completed}</b></div><div className="flex justify-between"><span>Overdue</span><b className="text-red-600">{data.team_progress.tasks.overdue}</b></div></div>
            </div>
            <div className="rounded-md border border-zinc-100 p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-emerald-600">Publishing</div>
              <div className="space-y-1 text-sm text-zinc-600"><div className="flex justify-between"><span>Posted today</span><b>{data.team_progress.publishing.posted_today}</b></div><div className="flex justify-between"><span>Awaiting post</span><b>{data.team_progress.publishing.pending}</b></div><div className="flex justify-between"><span>Stories</span><b>{data.team_progress.publishing.stories}</b></div></div>
            </div>
          </div>
        </Section>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="📢 Today's Content" subtitle="Planned vs posted today" action={<button onClick={() => navigate("/publishing")} className="text-xs font-medium text-zinc-500 hover:text-zinc-900">Publishing →</button>}>
          <div className="grid grid-cols-4 gap-3">
            <MiniStat label="Planned" value={data.today.content_planned.length} tone="blue" />
            <MiniStat label="Posted" value={data.today.content_posted.length} tone="green" />
            <MiniStat label="Pending" value={data.today.content_pending.length} tone="orange" />
            <MiniStat label="Delayed" value={data.today.content_delayed.length} tone="red" />
          </div>
        </Section>
        <Section title="📱 Today's Stories" subtitle={`${s.stories.covered}/${s.stories.total_clients} clients covered · ${s.stories.posted} stories`} action={<Image className="h-4 w-4 text-zinc-400" />}>
          {data.stories.missing.length === 0 ? (
            <div className="py-2 text-sm text-emerald-600">All clients covered today 🎉</div>
          ) : (
            <div>
              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-red-600">Stories Missing</div>
              <div className="flex flex-wrap gap-2">
                {data.stories.missing.map((m) => <Badge key={m.id} color="red">{m.name}</Badge>)}
              </div>
            </div>
          )}
        </Section>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="🔴 Overdue Tasks" danger action={<button onClick={() => navigate("/tasks")} className="text-xs font-medium text-zinc-500 hover:text-zinc-900">All tasks →</button>}>
          <div className="space-y-2">
            {data.tasks.overdue.length === 0 && <div className="py-2 text-center text-sm text-zinc-400">No overdue tasks</div>}
            {data.tasks.overdue.map((tk) => (
              <div key={tk.id} className="flex items-center justify-between rounded-md border border-red-100 px-3 py-2">
                <div><div className="text-sm font-medium">{tk.title}</div><div className="text-xs text-zinc-500">{tk.assignee_name}{tk.client_name ? ` · ${tk.client_name}` : ""}</div></div>
                <Badge color="red">{tk.days_overdue}d overdue</Badge>
              </div>
            ))}
          </div>
        </Section>
        <Section title="🟠 Tasks Due Today" warn>
          <div className="space-y-2">
            {data.tasks.due_today.length === 0 && <div className="py-2 text-center text-sm text-zinc-400">Nothing due today</div>}
            {data.tasks.due_today.map((tk) => (
              <div key={tk.id} className="flex items-center justify-between rounded-md border border-amber-100 px-3 py-2">
                <div><div className="text-sm font-medium">{tk.title}</div><div className="text-xs text-zinc-500">{tk.assignee_name}</div></div>
                <Badge color="orange">Today</Badge>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="📸 Shoots" action={<button onClick={() => navigate("/shoots")} className="text-xs font-medium text-zinc-500 hover:text-zinc-900">View all →</button>}>
          <div className="grid grid-cols-3 gap-3">
            <MiniStat label="Today" value={s.shoots.today} tone="blue" />
            <MiniStat label="This Week" value={s.shoots.week} tone="blue" />
            <MiniStat label="Delivery Pending" value={s.shoots.delivery_pending} tone="orange" />
          </div>
          <div className="mt-3 space-y-1">
            {data.shoots.week.slice(0, 4).map((sh) => (
              <div key={sh.id} className="flex items-center gap-3 rounded-md px-2 py-1.5 text-sm hover:bg-zinc-50">
                <Camera className="h-4 w-4 text-zinc-400" />
                <span className="flex-1 truncate">{sh.shoot_type}</span>
                <span className="text-xs text-zinc-500">{fmtDate(sh.date)} · {sh.start_time}</span>
              </div>
            ))}
          </div>
        </Section>

        {isManager && (
          <Section title="💰 Payments" action={<IndianRupee className="h-4 w-4 text-zinc-400" />}>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <MiniStat label="Due Today" value={inrShort(money.due_today)} tone="orange" />
              <MiniStat label="Due This Week" value={inrShort(money.due_this_week)} tone="orange" />
              <MiniStat label="Overdue" value={inrShort(money.overdue)} tone="red" />
              <MiniStat label="Received (Month)" value={inrShort(money.received_this_month)} tone="green" />
              <MiniStat label="Outstanding" value={inrShort(money.total_outstanding)} tone="zinc" />
            </div>
            {data.payment_alerts.overdue.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {data.payment_alerts.overdue.slice(0, 4).map((p) => (
                  <button key={p.id} onClick={() => navigate("/payments?status=overdue")} className="flex w-full items-center justify-between rounded-md border border-red-100 px-3 py-2 text-left hover:bg-red-50">
                    <div><div className="text-sm font-medium">{p.client_name}</div><div className="text-xs text-red-600">{p.days} days overdue</div></div>
                    <span className="font-mono text-sm font-semibold">{inr(p.balance)}</span>
                  </button>
                ))}
              </div>
            )}
          </Section>
        )}
      </div>
    </div>
  );
}
